/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Atom, WritableAtom, atom } from 'jotai';
import { addOrUpdateEntityItem, removeEntityId } from 'jotai/entity';
import { atomFamily, useAtomCallback } from 'jotai/utils';
import { ViewLayoutsAtomFamilyProps, viewLayoutsAtomFamily, viewLayoutsIdsAtom } from 'jotai/viewLayouts';
import { VisualizationObject, visualizationIdsAtom, visualizationsAtomFamily } from 'jotai/visualization';
import { activeViewIdAtom, activeWorkspaceAtom } from 'jotai/workspace';
import { workspaceService } from 'services/workspace/WorkspaceService';
import { v4 as uuidv4 } from 'uuid';
import { ViewObject } from '.';
import { useCallback } from 'react';

const overwrittenViewIds = atom<string[]>([]);
export const viewIdsAtom = atom<string[], [string[]], void>(
  (get) => {
    if (get(overwrittenViewIds).length > 0) {
      return get(overwrittenViewIds);
    }
    const currentWorkspace = get(activeWorkspaceAtom);
    if (Object.keys(currentWorkspace).length > 0) {
      return [...get(activeWorkspaceAtom).workspace.views.allIds];
    } else {
      return [];
    }
  },
  (get, set, newViewIds: string[]) => {
    const previousWorkspace = get(activeWorkspaceAtom);
    previousWorkspace.workspace.views.allIds = [...newViewIds];
    set(overwrittenViewIds, [...newViewIds]);
    set(activeWorkspaceAtom, previousWorkspace);
  }
);

export const viewAtomFamily = atomFamily<string, WritableAtom<ViewObject, [ViewObject], void>>((id: string) =>
  atom<ViewObject, [ViewObject], void>(
    (get) => get(activeWorkspaceAtom).workspace.views.byId[id],
    (get, set, view: ViewObject) => {
      if (Object.keys(view).length === 0) {
        const previuosWorkspace = get(activeWorkspaceAtom);
        removeEntityId(previuosWorkspace.workspace.views, id);
        set(activeWorkspaceAtom, previuosWorkspace);
        workspaceService.updateWorkspace(previuosWorkspace.workspace);
      } else {
        const previousWorkspace = get(activeWorkspaceAtom);
        addOrUpdateEntityItem(previousWorkspace.workspace.views, view, view.id);
        set(activeWorkspaceAtom, { ...previousWorkspace });
        workspaceService.updateWorkspace(previousWorkspace.workspace);
      }
    }
  )
);

export const activeViewSelector = atom<ViewObject>((get) => {
  const activeViewId = get(activeViewIdAtom);
  return get(viewAtomFamily(activeViewId));
});

export const isActiveViewSelectorFamily = atomFamily<string, Atom<boolean>>((viewId: string) =>
  atom<boolean>((get) => get(activeViewIdAtom) === viewId)
);

export const useCreateView = () => {
  return useAtomCallback(
    useCallback((get, set) => {
      const name = get(createViewNameAtom);
      if (name) {
        const nextViewLayoutsId = uuidv4();
        // Create new Layout.
        const viewLayoutIds = get(viewLayoutsIdsAtom);
        set(viewLayoutsIdsAtom, [...viewLayoutIds, nextViewLayoutsId]);
        set(viewLayoutsAtomFamily(nextViewLayoutsId), {
          viewLayouts: [],
          isReset: false,
        } as ViewLayoutsAtomFamilyProps);

        // create new view
        const nextId = uuidv4();
        const previousViewIds = get(viewIdsAtom);
        set(viewIdsAtom, [...previousViewIds, nextId]);
        set(viewAtomFamily(nextId), {
          id: nextId,
          name: name.trim(),
          visualizations: [],
          layouts: nextViewLayoutsId,
        });

        // make new view active
        set(activeViewIdAtom, nextId);
        set(createViewNameAtom, '');
      }
    }, [])
  );
};

export const useDeleteView = (viewId: string) => {
  return useAtomCallback(
    useCallback(
      (get, set) => {
        const view = get(viewAtomFamily(viewId));

        // Determine if a new Active View is needed.
        const isActive = get(isActiveViewSelectorFamily(viewId));
        let activeViewId = '';

        if (isActive) {
          const viewIds = get(viewIdsAtom);
          if (viewIds.length > 1) {
            let activeViewIndex = viewIds.indexOf(viewId);
            activeViewId =
              activeViewIndex === viewIds.length - 1 ? viewIds[activeViewIndex - 1] : viewIds[activeViewIndex + 1];
          }
        }

        // Remove all Visualizations under this View.
        view.visualizations.map((id) => set(visualizationsAtomFamily(id), {} as VisualizationObject));

        // Remove all Visualization ids under this View.
        const prevViewsVisualizationIds = get(visualizationIdsAtom);
        const newVisualizationIds = prevViewsVisualizationIds.filter((id) => !view.visualizations.includes(id));
        set(visualizationIdsAtom, newVisualizationIds);

        // Remove Layouts for this View.
        set(viewLayoutsAtomFamily(view.layouts), {
          isReset: true,
          viewLayouts: [],
        });

        // Remove Layouts id for this View.
        const prevLayouts = get(viewLayoutsIdsAtom);
        const newLayouts = prevLayouts.filter((id) => view.layouts !== id);
        set(viewLayoutsIdsAtom, newLayouts);

        // Reset (delete) View atom.
        set(viewAtomFamily(viewId), {} as ViewObject);

        // Remove View id from list.
        const prevViewIds = get(viewIdsAtom);
        const newViewIds = prevViewIds.filter((id) => id !== viewId);
        set(viewIdsAtom, newViewIds);

        // Reassign Active View if needed.
        if (isActive) {
          set(activeViewIdAtom, activeViewId);
        }
        return;
      },
      [viewId]
    )
  );
};

export const createViewNameAtom = atom<string>('');
